import { Button, FieldLabel, RadioGroup, RadioGroupItem } from '@arcadiapower/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Organization, Provider } from '@schema/schemas';
import { TrackEvents, trackEvent } from '@client/utils/analytics';
import { ty } from '@client/styles/typography';
import { ContentSectionFullHeight, Footer } from '../connect-container';
import { StyledButtonContainer, Stack } from './select-data-access.style';
import { Form } from '../form';
import { OrganizationTerms } from '../organization-terms';

export type Props = {
  provider: Provider;
  organization: Organization;
  onSubmit: ({ isMeterSubmission }: { isMeterSubmission: boolean }) => void;
};

const smartMeterTexasUrl = 'https://www.smartmetertexas.com/aboutus';
export const SelectDataAccessComponent = ({
  provider,
  organization,
  onSubmit,
}: Props): JSX.Element => {
  const [isIntervalsSelected, setIsIntervalsSelected] = useState<boolean>(false);

  const { t } = useTranslation('connect', { keyPrefix: 'components.selectDataAccess' });

  const handleSubmit = () => {
    trackEvent(TrackEvents.DATA_ACCESS_METHOD_SELECTED, {
      method: isIntervalsSelected ? 'METER' : 'CREDENTIAL',
    });
    onSubmit({ isMeterSubmission: isIntervalsSelected });
  };
  return (
    <>
      <ContentSectionFullHeight withTopPadding={true}>
        <Stack>
          <h3 className={ty('heading900')} data-testid="providerName">
            {provider.name}
          </h3>
          <p className={ty('default')}>{t('description')}</p>
          <Form onSubmit={handleSubmit} id="dataAccessMethodForm">
            <RadioGroup
              className="gap-4"
              name="dataAccessMethod"
              onValueChange={value => {
                setIsIntervalsSelected(value === 'intervals');
              }}
              value={isIntervalsSelected ? 'intervals' : 'monthly'}
            >
              <StyledButtonContainer isSelected={!isIntervalsSelected}>
                <div className="flex items-start gap-2">
                  <RadioGroupItem className="mt-0.5" id="data-access-monthly" value="monthly" />
                  <FieldLabel className="flex-col items-start" htmlFor="data-access-monthly">
                    <span className={ty('heading500')}>{t('monthlyTitle')}</span>
                    <span className={ty('paragraph500')}>{t('monthlyDescription')}</span>
                  </FieldLabel>
                </div>
              </StyledButtonContainer>
              <StyledButtonContainer isSelected={isIntervalsSelected}>
                <div className="flex items-start gap-2">
                  <RadioGroupItem className="mt-0.5" id="data-access-intervals" value="intervals" />
                  <FieldLabel className="flex-col items-start" htmlFor="data-access-intervals">
                    <span className={ty('heading500')}>{t('intervalsTitle')}</span>
                    <span className={ty('paragraph500')}>
                      {t('intervalsDescription')}
                      <Button variant="link" asChild className="h-auto rounded-sm p-0">
                        <a href={smartMeterTexasUrl} target="_blank" rel="noreferrer">
                          {t('smartMeterTexas')}
                        </a>
                      </Button>
                    </span>
                  </FieldLabel>
                </div>
              </StyledButtonContainer>
            </RadioGroup>
          </Form>
        </Stack>
      </ContentSectionFullHeight>
      <Footer>
        <OrganizationTerms organization={organization} />
        <Button
          form="dataAccessMethodForm"
          aria-label={t('submit')}
          type="submit"
          className="w-full"
          variant="inverse"
        >
          {t('submit')}
        </Button>
      </Footer>
    </>
  );
};
